import React from "react";
import {
  BarChart3,
  CheckCircle2,
  Loader2,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";

const formatCount = (value) => Number(value || 0).toLocaleString("en-IN");

const formatRunTime = (run) => {
  const value = run?.completed_at || run?.started_at || run?.queued_at;
  return value ? new Date(value).toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
  }) : "—";
};

export default function ReviewLibraryPanel() {
  const [status, setStatus] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [starting, setStarting] = React.useState(false);
  const [campaignStarting, setCampaignStarting] = React.useState(false);

  const loadStatus = React.useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) {
      setLoading(true);
    }
    try {
      const { data } = await api.get("/admin/review-library/status");
      setStatus(data);
    } catch (error) {
      if (!quiet) {
        toast.error(formatError(error));
      }
    } finally {
      if (!quiet) {
        setLoading(false);
      }
    }
  }, []);

  React.useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  React.useEffect(() => {
    if (!status?.active_run) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      loadStatus({ quiet: true });
    }, 2500);
    return () => window.clearInterval(timer);
  }, [loadStatus, status?.active_run]);

  const startReplenishment = async () => {
    setStarting(true);
    try {
      await api.post("/admin/review-library/replenish", { batch_limit: 4 });
      toast.success("Review library batch started.");
      await loadStatus({ quiet: true });
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setStarting(false);
    }
  };

  const startCoverageCampaign = async () => {
    setCampaignStarting(true);
    try {
      await api.post("/admin/review-library/campaign/start", {});
      toast.success("Coverage campaign started.");
      await loadStatus({ quiet: true });
    } catch (error) {
      toast.error(formatError(error));
    } finally {
      setCampaignStarting(false);
    }
  };

  const readyProgress = status?.target_drafts
    ? Math.min(100, Math.round((status.ready_drafts / status.target_drafts) * 100))
    : 0;
  const coverage = status?.coverage || {};
  const activity = status?.recent_activity || {};
  const active = status?.active_run;
  const latestRun = status?.recent_runs?.[0];
  const campaign = status?.campaign;
  const campaignActive = campaign?.status === "queued" || campaign?.status === "running";

  return (
    <section
      className="mb-6 border border-line bg-white p-5 sm:p-6 rounded-2xl"
      data-testid="review-library-panel"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-[#EDF6EA] p-2.5 text-[#2D4A22] flex-shrink-0">
            <BarChart3 size={16} strokeWidth={1.8} />
          </div>
          <div>
            <p className="overline text-[10px]">Owner controls</p>
            <h3 className="font-serif text-xl text-ink mt-1">
              Review Draft Library
            </h3>
            <p className="text-xs text-muted2 mt-1">
              Pre-generated only. Customer review requests never wait for a
              live draft.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="review-library-refresh-button"
            onClick={() => loadStatus()}
            disabled={loading}
            className="btn-outline-maroon !px-3 !py-2 !text-xs inline-flex items-center gap-1.5"
            title="Refresh library status"
            aria-label="Refresh library status"
          >
            <RefreshCw
              className={loading ? "animate-spin" : ""}
              size={14}
              strokeWidth={1.7}
            />
          </button>
          <button
            type="button"
            data-testid="review-library-replenish-button"
            onClick={startReplenishment}
            disabled={starting || Boolean(active)}
            className={
              "btn-maroon !px-4 !py-2 !text-xs inline-flex items-center gap-1.5 " +
              "disabled:opacity-50"
            }
          >
            {starting || active ? (
              <Loader2 className="animate-spin" size={13} />
            ) : (
              <ShieldCheck size={13} />
            )}
            {active ? "Batch Running" : "Replenish Library"}
          </button>
          <button
            type="button"
            data-testid="review-library-coverage-campaign-button"
            onClick={startCoverageCampaign}
            disabled={campaignStarting || Boolean(active) || campaignActive}
            className={
              "btn-outline-maroon !px-4 !py-2 !text-xs inline-flex items-center gap-1.5 " +
              "disabled:opacity-50"
            }
          >
            {campaignStarting || campaignActive ? (
              <Loader2 className="animate-spin" size={13} />
            ) : (
              <BarChart3 size={13} />
            )}
            {campaignActive ? "Coverage Running" : "Continue Coverage"}
          </button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div
          className="border border-line/70 rounded-xl p-3"
          data-testid="review-library-ready-count"
        >
          <p className="overline text-[10px]">Ready drafts</p>
          <p className="font-serif text-2xl text-ink mt-1">
            {formatCount(status?.ready_drafts)}
          </p>
          <p className="text-[11px] text-muted2 mt-1">
            of {formatCount(status?.target_drafts)} target
          </p>
        </div>
        <div
          className="border border-line/70 rounded-xl p-3"
          data-testid="review-library-unused-count"
        >
          <p className="overline text-[10px]">Unused drafts</p>
          <p className="font-serif text-2xl text-ink mt-1">
            {formatCount(status?.unused_drafts)}
          </p>
          <p className="text-[11px] text-muted2 mt-1">available instantly</p>
        </div>
        <div
          className="border border-line/70 rounded-xl p-3"
          data-testid="review-library-coverage-count"
        >
          <p className="overline text-[10px]">Coverage</p>
          <p className="font-serif text-2xl text-ink mt-1">
            {formatCount(coverage.covered_combinations)}
            /
            {formatCount(coverage.total_combinations)}
          </p>
          <p className="text-[11px] text-muted2 mt-1">combinations at full depth</p>
          <p className="text-[11px] text-muted2 mt-1">
            {formatCount(coverage.under_target_combinations)} still below target
          </p>
        </div>
        <div
          className="border border-line/70 rounded-xl p-3"
          data-testid="review-library-activity-count"
        >
          <p className="overline text-[10px]">Last 24 hours</p>
          <p className="font-serif text-2xl text-ink mt-1">
            {formatCount(activity.accepted)}
          </p>
          <p className="text-[11px] text-muted2 mt-1">
            accepted · {formatCount(activity.rejected)} rejected
          </p>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between text-[11px] text-muted2 mb-1.5">
          <span data-testid="review-library-progress-label">Generation progress</span>
          <span data-testid="review-library-progress-percent">{readyProgress}%</span>
        </div>
        <div
          className="h-2 bg-ivory rounded-full overflow-hidden"
          data-testid="review-library-progress-track"
        >
          <div
            className="h-full bg-[#2D4A22] rounded-full transition-[width] duration-500"
            style={{ width: `${readyProgress}%` }}
            data-testid="review-library-progress-fill"
          />
        </div>
      </div>

      <div className="mt-4 grid lg:grid-cols-2 gap-3 text-sm">
        <div
          className="border border-line/70 rounded-xl p-3"
          data-testid="review-library-run-status"
        >
          <p className="overline text-[10px] mb-1">Generation status</p>
          {active ? (
            <p className="inline-flex items-center gap-1.5 text-[#2D4A22] font-medium">
              <Loader2 className="animate-spin" size={14} />
              {active.status === "queued" ? "Queued" : "Running"}
            </p>
          ) : latestRun ? (
            <p className="inline-flex items-center gap-1.5 text-ink font-medium">
              <CheckCircle2 className="text-[#2D4A22]" size={14} />
              Latest batch: {latestRun.created} accepted at {formatRunTime(latestRun)}
            </p>
          ) : (
            <p className="text-muted2 text-xs">No batch activity recorded yet.</p>
          )}
        </div>
        <div
          className="border border-line/70 rounded-xl p-3"
          data-testid="review-library-rejection-reasons"
        >
          <p className="overline text-[10px] mb-1">Recent rejection reasons</p>
          {activity.rejection_reasons?.length ? (
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted2">
              {activity.rejection_reasons.map((item) => (
                <span
                  key={item.reason}
                  data-testid={`review-rejection-${item.reason}`}
                >
                  {item.reason.replace(/_/g, " ")}: {formatCount(item.count)}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-muted2 text-xs">
              No rejections in the last 24 hours.
            </p>
          )}
        </div>
      </div>

      <div
        className="mt-3 border border-line/70 rounded-xl p-3"
        data-testid="review-library-campaign-status"
      >
        <p className="overline text-[10px] mb-1">Coverage campaign</p>
        {campaign ? (
          <div className="text-xs text-muted2 flex flex-wrap gap-x-3 gap-y-1">
            <span data-testid="review-library-campaign-state">
              State: {campaign.status}
            </span>
            <span data-testid="review-library-campaign-batches">
              Batches: {formatCount(campaign.batches_completed)}
            </span>
            <span data-testid="review-library-campaign-accepted">
              Accepted: {formatCount(campaign.accepted)}
            </span>
            <span data-testid="review-library-campaign-attempts">
              Attempts: {formatCount(campaign.attempts)}
            </span>
            <span data-testid="review-library-campaign-shortfall">
              Missing: {formatCount(campaign.coverage?.missing_unused_drafts)}
            </span>
          </div>
        ) : (
          <p className="text-xs text-muted2">
            Start a bounded campaign to work through exact aspect coverage.
          </p>
        )}
      </div>
    </section>
  );
}